'use client';

import { FunctionComponent, useState, useRef } from 'react';
import { Alert, Button } from 'react-bootstrap';
import { useTranslate } from '@xFrame4/components/Hooks';
import XForm from '@xFrame4/components/common/XForm';
import FieldEditor, { FieldEditorLabelPosition, FieldEditorType } from '@xFrame4/components/common/FieldEditor';
import ContactFormEntry from '@business/contact/ContactFormEntry';
import { ContactFormEntryStatus } from '@business/contact/generated/ContactFormEntry.generated';
import ContactInfoItem from './ContactInfoItem';
import AppTracker from '@components/tracking/AppTracker';

export interface ContactFormProps
{

}

/**
 * Component: a contact form for the hotel. Uses xForm.
 */
const ContactForm: FunctionComponent<ContactFormProps> = (props) =>
{
    const t = useTranslate();
    const formRef = useRef<any>(null);

    const [name, setName] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [subject, setSubject] = useState<string>('');
    const [message, setMessage] = useState<string>('');
    const [errorMessages, setErrorMessages] = useState<string[]>([]);
    const [successMessage, setSuccessMessage] = useState<string>();
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    /** Submit the form. */
    const submitForm = () =>
    {
        if (formRef.current != null) (formRef.current as any).submit();
    }

    /** Validate the form. */
    const validateForm = () =>
    {
        setErrorMessages([]);

        if (!name.trim())
        {
            setErrorMessages(prev => [...prev, t('NAME_IS_REQUIRED')]);
        }

        if (!email.trim())
        {
            setErrorMessages(prev => [...prev, t('EMAIL_IS_REQUIRED')]);
        }
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
        {
            setErrorMessages(prev => [...prev, t('EMAIL_IS_INVALID')]);
        }

        if (!subject.trim())
        {
            setErrorMessages(prev => [...prev, t('SUBJECT_IS_REQUIRED')]);
        }

        if (!message.trim())
        {
            setErrorMessages(prev => [...prev, t('MESSAGE_IS_REQUIRED')]);
        }

        return errorMessages.length === 0;
    }

    /** Save the contact message to the database. */
    const saveContactMessage = async () =>
    {
        setIsSubmitting(true);
        try
        {
            let contactFormEntry = new ContactFormEntry();
            contactFormEntry.name = name;
            contactFormEntry.email = email;
            contactFormEntry.subject = subject;
            contactFormEntry.message = message;
            contactFormEntry.status = ContactFormEntryStatus.NEW;

            await contactFormEntry.save();

            // Track the contact form submission
            await AppTracker.trackContactFormSubmit(email);

            // Reset form
            setName('');
            setEmail('');
            setSubject('');
            setMessage('');
            setSuccessMessage(t('CONTACT_FORM_SUCCESS_MESSAGE'));
            setErrorMessages([]);
        }
        catch (error)
        {
            setErrorMessages([t('CONTACT_FORM_ERROR_MESSAGE')]);
        }
        finally
        {
            setIsSubmitting(false);
        }
    }

    /** Render */
    return (
        <div className="contact-form">
            <ContactInfoItem
                icon="fa-regular fa-user"
                title={t('CONTACT_US')}
                valueContent={
                    <XForm
                        ref={formRef}
                        onValidate={() => validateForm()}
                        onSubmit={() => saveContactMessage()}
                    >
                        <FieldEditor
                            type={FieldEditorType.Text}
                            field="name"
                            labelPosition={FieldEditorLabelPosition.Hidden}
                            placeholder={t('NAME')}
                            isRequired={true}
                            value={name}
                            onValueChanged={(field, value) => setName(value)}
                        />

                        <FieldEditor
                            type={FieldEditorType.Email}
                            field="email"
                            labelPosition={FieldEditorLabelPosition.Hidden}
                            placeholder={t('EMAIL')}
                            isRequired={true}
                            value={email}
                            onValueChanged={(field, value) => setEmail(value)}
                        />

                        <FieldEditor
                            type={FieldEditorType.Text}
                            field="subject"
                            labelPosition={FieldEditorLabelPosition.Hidden}
                            placeholder={t('SUBJECT')}
                            isRequired={true}
                            value={subject}
                            onValueChanged={(field, value) => setSubject(value)}
                        />

                        <FieldEditor
                            type={FieldEditorType.Textarea}
                            field="message"
                            labelPosition={FieldEditorLabelPosition.Hidden}
                            placeholder={t('MESSAGE')}
                            isRequired={true}
                            value={message}
                            onValueChanged={(field, value) => setMessage(value)}
                        />

                        <div className="messages mb-3">
                            {errorMessages.length > 0 &&
                                <div className="error-messages">
                                    {errorMessages.map((errorMessage, index) => (
                                        <Alert key={index} variant="danger">{errorMessage}</Alert>
                                    ))}
                                </div>
                            }
                            {successMessage &&
                                <div className="success-message">
                                    <Alert variant="success">{successMessage}</Alert>
                                </div>
                            }
                        </div>

                        <Button
                            variant="primary"
                            onClick={submitForm}
                            disabled={isSubmitting}
                            className="btn-lg"
                        >
                            {isSubmitting ? t('SENDING') : t('SEND_MESSAGE')}
                        </Button>
                    </XForm>
                }
            />
        </div>
    );
}

export default ContactForm;